import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { DollarSign, RefreshCw, X, Menu, User, LogOut, Settings } from 'lucide-react';
import { LoadingSpinner } from './ui';
import logoImage from '../Oxo Devisavi  Logo.webp';

const Header = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchCashSummary();
    
    // Update every 15 seconds for more frequent updates
    intervalRef.current = setInterval(() => {
      fetchCashSummary(true);
    }, 15000);
    
    // Also refresh when page becomes visible (user switches back to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchCashSummary(true);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsProfileMenuOpen(false);
  }, [location.pathname]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('button[aria-label="User profile menu"]')
      ) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  const fetchCashSummary = async (silent = false) => {
    try {
      if (!silent) {
        setIsRefreshing(true);
      }
      const response = await axios.get('https://backend.divisavi.org/api/help-seekers/cash-summary');
      const amount = response.data?.totalAmount;
      // Ensure we have a valid number
      const numAmount = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
      
      // Only update if value changed to avoid unnecessary re-renders
      if (numAmount !== totalAmount) {
        setTotalAmount(numAmount);
      }
      
      setLastUpdated(new Date());
      setLoading(false);
    } catch (error) {
      console.error('Error fetching cash summary:', error);
      if (!loading) {
        // Only reset to 0 if we're not in initial loading state
        setTotalAmount(prev => prev);
      } else {
        setTotalAmount(0);
      }
      setLoading(false);
    } finally {
      setIsRefreshing(false);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const getDashboardPath = () => {
    if (!user) return '/admin/login';
    switch (user.role) {
      case 'administrator':
        return '/admin';
      case 'coordinator':
        return user.isRegistrationComplete ? '/coordinator/dashboard' : '/coordinator/register';
      case 'donor':
        return '/donor/dashboard';
      default:
        return '/admin/login';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'administrator':
        return 'Administrator';
      case 'coordinator':
        return 'Coordinator';
      case 'donor':
        return 'Donor';
      default:
        return role;
    }
  };

  const handleLogout = () => {
    logout();
    setIsProfileMenuOpen(false);
    setIsMobileMenuOpen(false);
    window.location.href = '/admin/login';
  };

  const navigationItems = [
    { path: '/', label: 'Home' },
    { path: '/help', label: 'Request Help' },
    { path: '/donate', label: 'Donate' },
    { path: '/volunteer', label: 'Volunteer' },
    { path: '/map', label: 'Map' },
    // { path: '/centers', label: 'Find Centers' },
  ];

  return (
    <header className="bg-white/95 backdrop-blur-sm shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <motion.div 
            className="flex items-center"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Link 
              to="/" 
              className="shrink-0 flex items-center gap-2 focus:outline-none p-1"
              aria-label="Flood Relief System Home"
            >
              <img 
                src={logoImage} 
                alt="Divisavi Logo" 
                className="h-10 w-auto object-contain"
              />
              </Link>
          </motion.div>
          
          <div className="flex items-center gap-4 lg:gap-6">
            {/* Total Received Amount Display */}
            {/* <motion.div 
              className="hidden lg:flex items-center gap-2 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg px-4 py-2 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              onClick={() => fetchCashSummary(false)}
              title="Click to refresh"
            >
              <DollarSign size={18} className="text-green-600 shrink-0" />
              <span className="text-green-700 font-semibold text-sm">Total Received:</span>
              {loading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <AnimatePresence mode="wait">
                  <motion.span 
                    key={totalAmount}
                    className="text-green-800 font-bold text-base"
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    {typeof totalAmount === 'number' && !isNaN(totalAmount) 
                      ? totalAmount.toLocaleString('en-US', { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        })
                      : '0.00'} LKR
                  </motion.span>
                </AnimatePresence>
              )}
              <motion.div
                animate={{ rotate: isRefreshing ? 360 : 0 }}
                transition={{ 
                  duration: isRefreshing ? 1 : 0,
                  repeat: isRefreshing ? Infinity : 0,
                  ease: "linear"
                }}
                className="ml-1"
              >
                <RefreshCw 
                  size={14} 
                  className={`text-green-600 shrink-0 transition-opacity ${
                    isRefreshing ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'
                  }`}
                />
              </motion.div>
            </motion.div> */}

            <nav className="hidden md:flex items-center gap-1" role="navigation" aria-label="Main navigation">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 min-h-[44px] flex items-center ${
                    isActive(item.path)
                      ? 'text-[#5f07ff] bg-[#5f07ff1a]'
                      : 'text-gray-700 hover:text-[#5f07ff] hover:bg-[#5f07ff1a]'
                  } focus:outline-none`}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                >
                  {item.label}
                  {isActive(item.path) && (
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#5f07ff] rounded-full"
                      layoutId="activeTab"
                      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    />
                  )}
                </Link>
              ))}
            </nav>

            {/* User Profile - Desktop */}
            {isAuthenticated && user && (
              <div className="hidden md:block relative">
                <button
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-[#5f07ff] hover:bg-[#5f07ff1a] transition-all duration-200 focus:outline-none min-h-[44px]"
                  aria-label="User profile menu"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#5f07ff] to-purple-600 flex items-center justify-center text-white font-semibold text-xs">
                    {user.fullName?.charAt(0).toUpperCase() || user.username?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <span className="hidden lg:block max-w-[120px] truncate">{user.fullName || user.username}</span>
                </button>

                {/* Profile Dropdown */}
                <AnimatePresence>
                  {isProfileMenuOpen && (
                    <>
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-40"
                        onClick={() => setIsProfileMenuOpen(false)}
                      />
                      <motion.div
                        ref={profileMenuRef}
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden"
                      >
                        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-[#5f07ff] to-purple-600 text-white">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white font-semibold text-lg">
                              {user.fullName?.charAt(0).toUpperCase() || user.username?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold truncate">{user.fullName || user.username}</p>
                              <p className="text-sm text-white/80 truncate">{user.email || user.username}</p>
                              <p className="text-xs text-white/70 mt-1">{getRoleLabel(user.role)}</p>
                            </div>
                          </div>
                        </div>
                        <div className="py-2">
                          <Link
                            to={getDashboardPath()}
                            onClick={() => setIsProfileMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            <Settings size={16} className="text-gray-500" />
                            <span>Dashboard</span>
                          </Link>
                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <LogOut size={16} />
                            <span>Logout</span>
                          </button>
                        </div>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 focus:outline-none min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors"
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <AnimatePresence mode="wait">
                {isMobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="w-6 h-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="w-6 h-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu - Overlay Above Page */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            
            {/* Menu Panel - Fixed Above Page */}
            <motion.div
              ref={menuRef}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="fixed top-16 left-0 right-0 bg-white shadow-2xl z-50 md:hidden border-b border-gray-200"
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-3 h-[100vh] overflow-y-auto">
                {/* Mobile Total Amount Display */}
                <motion.div 
                  className="flex items-center gap-2 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg px-4 py-3 shadow-sm"
                  onClick={() => fetchCashSummary(false)}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <DollarSign size={18} className="text-green-600 shrink-0" />
                  <div className="flex-1">
                    <span className="text-green-700 font-semibold text-sm block">Total Received:</span>
                    {loading ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <span className="text-green-800 font-bold text-base">
                        {typeof totalAmount === 'number' && !isNaN(totalAmount) 
                          ? totalAmount.toLocaleString('en-US', { 
                              minimumFractionDigits: 2, 
                              maximumFractionDigits: 2 
                            })
                          : '0.00'} LKR
                      </span>
                    )}
                  </div>
                  <RefreshCw 
                    size={14} 
                    className={`text-green-600 shrink-0 transition-opacity ${
                      isRefreshing ? 'opacity-100 animate-spin' : 'opacity-50'
                    }`}
                  />
                </motion.div>

                {/* Navigation Links */}
                <nav className="space-y-1" role="navigation" aria-label="Mobile navigation">
                  {navigationItems.map((item, index) => (
                    <motion.div
                      key={item.path}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + (index * 0.05) }}
                    >
                      <Link
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`block px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
                          isActive(item.path)
                            ? 'text-primary-600 bg-primary-50 border-l-4 border-primary-600'
                            : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                        } focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                        aria-current={isActive(item.path) ? 'page' : undefined}
                      >
                        {item.label}
                      </Link>
                      
                    </motion.div>
                  ))}
                </nav>

                {/* User Profile - Mobile */}
                {isAuthenticated && user && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mt-4 pt-4 border-t border-gray-200"
                  >
                    <div className="px-4 py-3 bg-gradient-to-r from-[#5f07ff] to-purple-600 rounded-lg text-white mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white font-semibold text-lg">
                          {user.fullName?.charAt(0).toUpperCase() || user.username?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{user.fullName || user.username}</p>
                          <p className="text-sm text-white/80 truncate">{user.email || user.username}</p>
                          <p className="text-xs text-white/70 mt-1">{getRoleLabel(user.role)}</p>
                        </div>
                      </div>
                    </div>
                    <Link
                      to={getDashboardPath()}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-[#5f07ff] hover:bg-gray-50 transition-all duration-200 mb-2"
                    >
                      <Settings size={20} className="text-gray-500" />
                      <span>Dashboard</span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-red-600 hover:bg-red-50 transition-all duration-200"
                    >
                      <LogOut size={20} />
                      <span>Logout</span>
                    </button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;